import { FC, useState, Fragment, useEffect } from "react"
import styled from "styled-components"
import InnerImageZoom from "react-inner-image-zoom"
import "react-inner-image-zoom/lib/InnerImageZoom/styles.min.css"
import { useKeenSlider } from "keen-slider/react"
import "keen-slider/keen-slider.min.css"
import { useRouter } from "next/router"

import { FluidImage } from "@components/common"

interface SliderProps {
  slides: any[]
}

const MobileSlider: FC<SliderProps> = ({ slides }) => {
  const [currentSlide, setCurrentSlide] = useState(0)

  const router = useRouter()

  const [ref, slider] = useKeenSlider<HTMLUListElement>({
    initial: 0,
    slidesPerView: 1,
    slideChanged(s) {
      setCurrentSlide(s.details().relativeSlide)
    }
  })

  useEffect(() => {
    // The route has changed (first load or colorSwatch clicked), and/or the slider object has been loaded
    slider?.moveToSlide(0)
  }, [router.asPath, slider])

  return (
    <Slider ref={ref} className="keen-slider">
      {slides.map((slide: any, i: number) => {
        return (
          <Slide className={`keen-slider__slide number-slide${i + 1}`} key={i}>
            {slide?.node ? (
              <LayoutWrapper key={slide.node.id}>
                <StyledFluidImage
                  src={slide.node.transformedSrc}
                  alt={slide.node.altText}
                />
              </LayoutWrapper>
            ) : (
              <LayoutWrapper>
                <StyledVideo
                  key={slide.sys.id}
                  autoPlay
                  muted
                  loop
                  playsInline
                  src={slide.url}
                />
              </LayoutWrapper>
            )}
          </Slide>
        )
      })}
      {slider && (
        <Navigation>
          <Dots className="keen-slider__dots">
            {[...Array(slider.details().size).keys()].map(idx => {
              return (
                <Dot
                  key={idx}
                  onClick={() => {
                    slider.moveToSlideRelative(idx)
                  }}
                  $isActive={currentSlide === idx}
                />
              )
            })}
          </Dots>
        </Navigation>
      )}
    </Slider>
  )
}

const Slider = styled.ul`
  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    display: none;
  }
`

const Slide = styled.li``

const StyledFluidImage = styled(FluidImage)`
  position: absolute;
  top: 0;
  background: ${({ theme }) => theme.color.greyLight};
`

const Navigation = styled.div`
  position: absolute;
  bottom: 3rem;
  width: 100%;
  display: flex;
  justify-content: center;
`

const Dots = styled.div`
  display: inline-flex;
  margin: 0 auto;
`

interface ButtonProps {
  $isActive: boolean
}

const Dot = styled.button<ButtonProps>`
  display: block;
  margin: 0 0.5rem;
  padding: 0;
  width: 1rem;
  height: 1rem;
  opacity: ${({ $isActive }) => ($isActive ? 1 : 0.1)};
  background: ${({ theme }) => theme.color.black};

  :focus {
    outline: none;
  }
`

interface Props {
  media: {
    node: {
      id: string
      altText: string | null
      transformedSrc: string
    }
    sys: {
      id: string
    }
    url?: string
    contentType?: string
  }[]
}

export const ProductMedia: FC<Props> = ({ media }) => {
  return (
    <StyledProductMedia>
      <MobileSlider slides={media} />
      <MediaGrid>
        {media?.map(m => {
          return (
            <Fragment key={m?.node?.id || m?.sys?.id}>
              {m?.node && (
                <LayoutWrapper>
                  <ImageZoom
                    src={m.node.transformedSrc}
                    alt={m.node.altText}
                    zoomScale={0.6}
                  />
                </LayoutWrapper>
              )}
              {m?.url && (
                <LayoutWrapper>
                  <StyledVideo autoPlay muted loop playsInline>
                    <source src={m.url} type="video/mp4" />
                  </StyledVideo>
                </LayoutWrapper>
              )}
            </Fragment>
          )
        })}
      </MediaGrid>
    </StyledProductMedia>
  )
}

const StyledProductMedia = styled.div``

const MediaGrid = styled.div`
  display: none;

  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 2rem 2.3rem;
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.lg}px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 2rem 2.3rem;
  }
`

const LayoutWrapper = styled.div`
  position: relative;
  overflow: hidden;
  padding-top: 150%;
`

const ImageZoom = styled(InnerImageZoom)`
  position: absolute;
  top: 0;
  background: ${({ theme }) => theme.color.greyLight};

  span {
    display: none;
  }
`

const StyledVideo = styled.video`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 100%;
`
